import { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import {
  uploadDocumentForExtraction,
  getExtraction,
  deleteExtraction,
  parseExtractedFields,
  ExtractionResponse,
  ExtractedFields,
} from '../lib/documentExtractionApi';

interface PruefDokumentKarteProps {
  reportId: number | null;
  darkMode: boolean;
  onApply: (fields: ExtractedFields) => void;
}

type KarteStatus = 'leer' | 'verarbeitung' | 'fertig' | 'uebernommen' | 'fehler';

export function PruefDokumentKarte({ reportId, darkMode, onApply }: PruefDokumentKarteProps) {
  const [status, setStatus] = useState<KarteStatus>('leer');
  const [extraction, setExtraction] = useState<ExtractionResponse | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const appliedRef = useRef(false);
  const [showDetails, setShowDetails] = useState(false);

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const applyExtractionStatus = useCallback((ext: ExtractionResponse) => {
    setExtraction(ext);
    if (ext.status === 'done') setStatus('fertig');
    else if (ext.status === 'failed') setStatus('fehler');
    else if (ext.status === 'processing' || ext.status === 'pending') setStatus('verarbeitung');
  }, []);

  const startPolling = useCallback(() => {
    if (!reportId) return;
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const ext = await getExtraction(reportId);
        if (!ext) { stopPolling(); setStatus('leer'); return; }
        if (ext.status !== 'processing' && ext.status !== 'pending') {
          stopPolling();
          applyExtractionStatus(ext);
        }
      } catch { stopPolling(); }
    }, 2000);
  }, [reportId, applyExtractionStatus]);

  useEffect(() => {
    if (!reportId) return;
    getExtraction(reportId).then((ext) => {
      if (!ext) return;
      applyExtractionStatus(ext);
      if (ext.status === 'processing' || ext.status === 'pending') {
        startPolling();
      }
    }).catch(() => {});
    return () => stopPolling();
  }, [reportId, applyExtractionStatus, startPolling]);

  const handleFile = async (file: File) => {
    if (uploading || !reportId) return;
    setUploading(true);
    setStatus('verarbeitung');
    try {
      await uploadDocumentForExtraction(reportId, file);
      startPolling();
    } catch (err: unknown) {
      setStatus('fehler');
      const message = err instanceof Error ? err.message : 'Upload fehlgeschlagen';
      setExtraction({ id: 0, status: 'failed', originalFileName: file.name, extractedJson: null, errorMessage: message, createdAt: new Date().toISOString() });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDelete = async () => {
    stopPolling();
    appliedRef.current = false;
    setShowDetails(false);
    try { await deleteExtraction(reportId); } catch {}
    setStatus('leer');
    setExtraction(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleApply = () => {
    if (!extraction?.extractedJson || appliedRef.current) return;
    appliedRef.current = true;
    const fields = parseExtractedFields(extraction.extractedJson);
    onApply(fields);
    setStatus('uebernommen');
  };

  const summaryText = (ext: ExtractionResponse): string => {
    if (!ext.extractedJson) return '';
    const f = parseExtractedFields(ext.extractedJson);
    const parts = [
      f.schwachstellen.length && `${f.schwachstellen.length} Schwachstellen`,
      f.staerken.length && `${f.staerken.length} Stärken`,
      f.massnahmen.length && `${f.massnahmen.length} Maßnahmen`,
      f.beratungsziele.length && `${f.beratungsziele.length} Beratungsziele`,
      f.istZustand.length && `${f.istZustand.length} Ist-Zustand`,
      f.schwaechen.length && `${f.schwaechen.length} Schwächen`,
      f.berichtstitel?.trim() && 'Berichtstitel',
      f.geschaeftsgegenstand?.trim() && 'Geschäftsgegenstand',
      f.beratungsauftrag?.trim() && 'Beratungsauftrag',
    ].filter(Boolean);
    return parts.join(' · ');
  };

  const detailGroups = (ext: ExtractionResponse): { label: string; items: string[] }[] => {
    if (!ext.extractedJson) return [];
    const f = parseExtractedFields(ext.extractedJson);
    return [
      { label: 'Berichtstitel', items: f.berichtstitel?.trim() ? [f.berichtstitel.trim()] : [] },
      { label: 'Geschäftsgegenstand', items: f.geschaeftsgegenstand?.trim() ? [f.geschaeftsgegenstand.trim()] : [] },
      { label: 'Beratungsauftrag / Grund', items: f.beratungsauftrag?.trim() ? [f.beratungsauftrag.trim()] : [] },
      { label: 'Ist-Zustand', items: f.istZustand },
      { label: 'Stärken', items: f.staerken },
      { label: 'Schwächen', items: f.schwaechen },
      { label: 'Schwachstellen', items: f.schwachstellen },
      { label: 'Maßnahmen', items: f.massnahmen },
      { label: 'Beratungsziele', items: f.beratungsziele },
    ].filter((g) => g.items.length > 0);
  };

  return (
    <div className={`rounded-xl border p-3 ${card} mb-4`}>
      {status === 'leer' && (
        !reportId ? (
          <div className={`flex items-center gap-3 rounded-lg border border-dashed px-3 py-2.5 opacity-60 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className={`grid place-items-center w-8 h-8 rounded-lg shrink-0 ${darkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-400'}`}>
              <Upload className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-medium ${muted}`}>Bericht oder Prüfdokument hochladen</p>
              <p className={`text-xs ${muted} truncate`}>Zuerst Berichtstitel und Vorgangsnummer eintragen</p>
            </div>
          </div>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`group flex items-center gap-3 rounded-lg border border-dashed px-3 py-2.5 cursor-pointer transition-colors ${
              dragOver
                ? darkMode ? 'border-blue-500 bg-blue-500/10' : 'border-blue-400 bg-blue-50'
                : darkMode ? 'border-slate-600 hover:border-blue-500/70 hover:bg-slate-700/30' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50/40'
            }`}
          >
            <div className={`grid place-items-center w-8 h-8 rounded-lg shrink-0 transition-colors ${
              dragOver
                ? 'bg-blue-500 text-white'
                : darkMode ? 'bg-slate-700 text-blue-400 group-hover:bg-blue-500/20' : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'
            }`}>
              <Upload className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Bericht oder Prüfdokument hochladen</p>
              <p className={`text-xs ${muted} truncate`}>Optional · PDF/DOCX · max. 50 MB · KI trägt die Inhalte in die Felder ein</p>
            </div>
            <span className="shrink-0 inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors">
              Auswählen
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </div>
        )
      )}

      {status === 'verarbeitung' && (
        <div className="flex items-center gap-3 py-4">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">Dokument wird analysiert…</p>
            <p className={`text-xs ${muted}`}>{extraction?.originalFileName ?? 'Datei wird verarbeitet'}</p>
          </div>
        </div>
      )}

      {status === 'fertig' && extraction && (
        <div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">{extraction.originalFileName}</p>
                <p className={`text-xs ${muted} mt-0.5`}>{summaryText(extraction)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={handleApply}
                className="px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                In Felder übernehmen
              </button>
              <button onClick={handleDelete} className={`text-xs ${muted} hover:underline`}>Entfernen</button>
            </div>
          </div>

          {(() => {
            const groups = detailGroups(extraction);
            if (groups.length === 0) return null;
            return (
              <div className="mt-3">
                <button
                  onClick={() => setShowDetails((v) => !v)}
                  aria-expanded={showDetails}
                  className={`text-xs ${muted} hover:underline`}
                >
                  {showDetails ? 'Erkannte Inhalte ausblenden' : 'Erkannte Inhalte anzeigen'}
                </button>
                {showDetails && (
                  <div className={`mt-2 space-y-3 border-t pt-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    {groups.map((g) => (
                      <div key={g.label}>
                        <p className={`text-xs font-semibold mb-1 ${muted}`}>{g.label}</p>
                        <ul className="list-disc pl-5 space-y-0.5">
                          {g.items.map((it, i) => (
                            <li key={i} className="text-xs">{it}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {status === 'uebernommen' && (
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Felder wurden übernommen</p>
              <p className={`text-xs ${muted} mt-0.5`}>Eingetragene Felder können direkt in den jeweiligen Karten bearbeitet werden</p>
            </div>
          </div>
          <button onClick={handleDelete} className={`text-xs flex-shrink-0 ${muted} hover:underline`}>Entfernen</button>
        </div>
      )}

      {status === 'fehler' && (
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">Analyse fehlgeschlagen</p>
              <p className={`text-xs ${muted} mt-0.5`}>{extraction?.errorMessage ?? 'Unbekannter Fehler'}</p>
            </div>
          </div>
          <button
            onClick={handleDelete}
            className={`px-3 py-1.5 text-xs font-semibold border rounded-lg transition-colors flex-shrink-0 ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
          >
            Erneut versuchen
          </button>
        </div>
      )}
    </div>
  );
}
